import React, { MemoExoticComponent } from 'react';
import { useFela } from '../..';
import { WithDmId } from '../../utils/BaseProps';

interface DefaultStyleProps {
  theme: any;
}

type LinkProps = WithDmId & {
  /**
   * String oder Komponente, die als Basis-Element des Links verwendet wird.
   */
  as?: string | JSX.Element | MemoExoticComponent<any> | Function;
  /**
   * String oder Objekt, das als Label des Links verwendet wird.
   */
  children?: string | JSX.Element;
  /**
   * Boolean, mit dem der Link deaktiviert werden kann.
   */
  disabled?: boolean;
  /**
   * String, der die aufzurufende Url angibt.
   */
  href?: string;
  /**
   * Objekt, um das Styling des Links anzupassen.
   * Davon darf nur im Ausnahmefall Gebrauch gemacht werden!
   */
  style?: object;
  [otherProp: string]: any;
};

function defaultStyle({ theme }: DefaultStyleProps) {
  return {
    fontFamily: theme.typography.fontFamily.primary.main,
    fontSize: 'inherit',
    fontWeight: 400,
    cursor: 'pointer',
    display: 'inline-flex',
    alignSelf: 'flex-start',
    textDecoration: 'underline',
    color: theme.palette.color3.hex,
    ':hover': {
      color: theme.palette.color2.hex,
    },
    /* Reset styling in case the component gets rendered as Button */
    border: 'none',
    margin: 0,
    padding: 0,
    background: 'transparent',
    fontSmoothing: 'inherit',
    appearance: 'none',
  };
}

function resolveBaseElementProps(
  { as, disabled, href, style }: LinkProps,
  css: any,
) {
  switch (as) {
    case 'a':
      const maybeHref = disabled ? {} : { href };
      return { className: css([defaultStyle, style]), ...maybeHref };
    default:
      return { className: css([defaultStyle, style]) };
  }
}

export default function Link({
  as = 'a',
  disabled = false,
  href,
  style = {},
  children,
  dmId = 'dm-link',
  ...otherProps
}: LinkProps): React.ReactElement {
  const { css } = useFela();
  // @ts-ignore
  const baseElementProps = resolveBaseElementProps(
    { as, disabled, style, href },
    css,
  );
  const Component = as;
  return (
    // @ts-ignore
    <Component
      data-designsystem
      data-dmid={dmId}
      {...baseElementProps}
      {...otherProps}>
      {children}
    </Component>
  );
}
